// DashboardTimelineList — vertical milestone list for the program dashboard.
// Renders a thin sand connector running through circular markers, one per
// "milestone": program open, each deliverable's due date, and program close.
// Status is derived from the same submissions / statuses payload the rest of
// the dashboard already loads, so a deliverable that's been submitted shows
// a forest-filled checkmark, the next active one shows an amber dot with a
// soft halo, and future ones render as dashed-outline placeholders.

import { Text, View } from "react-native";

import { TimelineRail, type TimelineRailItem } from "../../aspects/TimelineRail";
import { PANE_THEMES } from "../../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import type {
  Deliverable,
  TeamDeliverableStatusRow,
  TeamSubmissionRow,
} from "../../../types/database";


type Status = "complete" | "active" | "upcoming";

type Item = {
  key: string;
  label: string;
  /** ISO date string. */
  iso: string | null;
  status: Status;
  /** Optional inline status note rendered next to the label. */
  note: string | undefined;
};

export type DashboardTimelineListProps = {
  opensAt: string | null;
  closesAt: string | null;
  deliverables: Deliverable[];
  submissions: TeamSubmissionRow[];
  statuses: TeamDeliverableStatusRow[];
  /** Override "now" for tests. */
  now?: number;
};

function fmtShort(iso: string | null): string {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "TBD";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function daysFromNow(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - now) / (24 * 60 * 60 * 1000));
}

export function DashboardTimelineList({
  opensAt,
  closesAt,
  deliverables,
  submissions,
  statuses,
  now: nowOverride,
}: DashboardTimelineListProps) {
  const now = nowOverride ?? Date.now();
  const submittedIds = new Set(submissions.map((s) => s.deliverable_id));
  const statusById = new Map<string, TeamDeliverableStatusRow>();
  for (const s of statuses) statusById.set(s.deliverable_id, s);

  const sorted = [...deliverables].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
  );

  // Find the first un-submitted, currently-unlocked deliverable — that's the
  // "active" one that gets the amber halo.
  const activeId = sorted.find(
    (d) =>
      !submittedIds.has(d.id) &&
      (d.opens_at == null || new Date(d.opens_at).getTime() <= now)
  )?.id ?? null;

  const items: Item[] = [];

  if (opensAt) {
    const opened = new Date(opensAt).getTime() <= now;
    items.push({
      key: "open",
      label: "Program opens",
      iso: opensAt,
      status: opened ? "complete" : "upcoming",
      note: undefined,
    });
  }

  for (const d of sorted) {
    let status: Status;
    let note: string | undefined = undefined;
    const stat = statusById.get(d.id);
    if (submittedIds.has(d.id)) {
      status = "complete";
      note =
        stat?.status === "passed"
          ? "Passed"
          : stat?.status === "failed"
          ? "Did not pass"
          : "Submitted";
    } else if (d.id === activeId) {
      status = "active";
      const days = daysFromNow(d.due_at, now);
      if (days != null) {
        if (days < 0) note = `Past due`;
        else if (days === 0) note = "In progress · due today";
        else if (days === 1) note = "In progress · 1d left";
        else note = `In progress · ${days}d left`;
      } else {
        note = "In progress";
      }
    } else {
      status = "upcoming";
      if (d.opens_at && new Date(d.opens_at).getTime() > now) {
        note = `Unlocks ${fmtShort(d.opens_at)}`;
      }
    }
    items.push({ key: d.id, label: d.name, iso: d.due_at, status, note });
  }

  if (closesAt) {
    const closed = new Date(closesAt).getTime() <= now;
    items.push({
      key: "close",
      label: "Program closes",
      iso: closesAt,
      status: closed ? "complete" : "upcoming",
      note: undefined,
    });
  }

  const railItems: TimelineRailItem[] = items.map((it) => ({
    key: it.key,
    label: it.label,
    dateLabel: fmtShort(it.iso),
    status: it.status,
    note: it.note,
  }));
  return <TimelineRail theme={STUDENT_THEME} items={railItems} />;
}


